import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../supabaseClient';

const ProfileHeader = ({ student, completionPercentage, onAvatarUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fonction pour gérer le clic sur l'image
  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  // Fonction d'upload vers Supabase
  const uploadAvatar = async (event) => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('Vous devez sélectionner une image.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${student.id}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // 1. Upload de l'image dans le Bucket 'avatars'
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Récupération de l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // 3. Mise à jour du profil dans la base de données
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date() })
        .eq('id', student.id);

      if (updateError) throw updateError;

      // 4. On prévient le parent pour rafraîchir l'affichage
      if (onAvatarUpdate) onAvatarUpdate();

    } catch (error) {
      alert('Erreur lors de l\'upload : ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6 shadow-sm animate-fade-in relative overflow-hidden">
      <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
        
        {/* AVATAR INTERACTIF */}
        <div className="relative group cursor-pointer" onClick={handleImageClick}>
          {/* Input caché */}
          <input
            type="file"
            id="avatar"
            ref={fileInputRef}
            accept="image/*"
            onChange={uploadAvatar}
            className="hidden"
            disabled={uploading}
          />

          <div className={`w-24 h-24 rounded-full border-4 border-background shadow-lg overflow-hidden flex items-center justify-center ${student.avatar ? 'bg-white' : 'bg-primary'}`}>
             {uploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
             ) : student.avatar ? (
               <img src={student.avatar} alt="Avatar" className="w-full h-full object-cover" />
             ) : (
               <span className="text-3xl font-bold text-white font-heading">{student.initials}</span>
             )}
          </div>
          
          {/* Overlay "Modifier" au survol */}
          <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Icon name="Camera" className="text-white" size={24} />
          </div>
          
          {/* Badge Vérifié */}
          <div className="absolute bottom-1 right-1 bg-green-500 text-white p-1 rounded-full border-2 border-card" title="Compte vérifié">
            <Icon name="Check" size={12} strokeWidth={4} />
          </div>
        </div>
        
        {/* Infos Texte */}
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-1">
            {student.name}
          </h1>
          <p className="text-muted-foreground mb-3">{student.email}</p>
          
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
            <div className="flex items-center text-sm text-muted-foreground bg-muted/50 px-3 py-1 rounded-full border border-border/50">
              <Icon name="MapPin" size={14} className="mr-1.5" />
              {student.city || 'Ville non renseignée'}
            </div>
            <div className="flex items-center text-sm text-muted-foreground bg-muted/50 px-3 py-1 rounded-full border border-border/50">
              <Icon name="Calendar" size={14} className="mr-1.5" />
              Membre depuis le {student.joinDate}
            </div>
          </div>
        </div>

        {/* Jauge de complétion */}
        <div className="w-full md:w-auto min-w-[200px] bg-muted/20 p-4 rounded-xl border border-border/50">
          <div className="flex justify-between items-end mb-2">
            <span className="text-sm font-medium text-muted-foreground">Profil complété</span>
            <span className={`text-xl font-bold ${completionPercentage === 100 ? 'text-green-600' : 'text-primary'}`}>
              {completionPercentage}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div 
              className={`h-2.5 rounded-full transition-all duration-1000 ease-out ${completionPercentage === 100 ? 'bg-green-500' : 'bg-primary'}`} 
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-right">
            {completionPercentage < 100 ? 'Complétez votre profil pour de meilleures recommandations' : 'Profil excellent !'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;